import omit from 'ramda/es/omit';
import { useRecoilCallback } from 'recoil';
import type { CallbackInterface } from 'recoil';
import { useCurrentCaseContext } from 'common/CurrentCase/CurrentCaseProvider';
import { hangingProtocolLayoutsState } from '../../ViewportsConfigurations/state';
import { layoutsForAllCasesState, updateCaseLayouts } from './viewerLoaderState';
import type { Layout, Layouts } from './viewerLoaderState';

/**
 * The only place that writes the viewer's two layout atoms.
 *
 * ## Why this module exists
 *
 * The viewer resolves a layout from two atoms, and `useLayouts` gives the second priority:
 *
 * ```
 * pick(windowIds, { ...defaults, ...hangingProtocolLayouts, ...layoutsForAllCases[caseId] })
 * ```
 *
 * - `hangingProtocolLayoutsState` is **derived**: the layout the active hanging protocol -- and, in
 *   stepped mode, the active *step* -- wants. It is recomputed as the step index changes, which is
 *   the only reason per-step layouts render at all.
 * - `layoutsForAllCasesState[caseId]` is the **user's manual override** for the case. It is
 *   case-scoped, `localForage`-persisted, and has no step dimension.
 *
 * That asymmetry is the whole problem. Writing one step's layout into the case-scoped override pins
 * *every* step to that layout, because stepping changes neither the case nor the window and nothing
 * clears the override on navigation. The same defect was found and fixed four separate times, each
 * at whichever call site the ticket happened to travel through -- VX-4624, VX-4732, VX-4768,
 * VX-4772 -- because thirteen call sites hand-coordinated these two atoms and did not agree on
 * whether to set or clear. `useSetViewerLayouts`, the previous attempt at centralising this, wrote
 * *both* and so encoded the bug as its contract.
 *
 * ## The rule
 *
 * **Applying a protocol- or step-derived layout must leave the user-override layer empty.** The step
 * owns the layout in stepped mode; a leftover override outranks it and leaks across steps.
 *
 * That is what makes step navigation come out right. A layout the user picks in clinical step mode
 * is routed to `writeLayoutToStep` (see `Viewer/index.tsx`), which stores it on the active step and
 * clears the override in the same write. By the time the user moves to another step there is no
 * override left to outrank it, so the arriving step renders its own layout. Navigation itself only
 * republishes the derived value -- it does not need to clear anything, because applying never
 * leaves anything behind.
 *
 * These functions are named for intent rather than mechanism so that a caller has to choose a
 * meaning, not a pair of `set` calls. Prefer them over touching either atom directly; a direct write
 * is how the fifth instance of this bug will arrive.
 *
 * Each takes Recoil's `set` so it can be called from inside an existing `useRecoilCallback` batch --
 * most callers are already in one, and splitting the writes across batches makes the grid render a
 * frame stale. {@link useViewerLayoutWriters} wraps them for component-level callers.
 */

type CaseScopedArgs = {
  /** Null is tolerated (the case may not be resolved yet); the override write is then skipped. */
  caseId: Nullable<string>;
  layouts: Layouts;
};

/**
 * Publishes the protocol's derived layout without touching the user's override.
 *
 * For the passive sync of `useHangingProtocol`'s memo into the atom. It deliberately does NOT clear
 * the override: that layer is the user's own layout choice, and every HP refetch would otherwise
 * throw it away.
 */
export function publishProtocolLayouts(
  set: CallbackInterface['set'],
  layouts: Nullable<Layouts>
): void {
  set(hangingProtocolLayoutsState, layouts);
}

/**
 * Makes a protocol- or step-derived layout the layout in effect.
 *
 * Publishes it and clears the user override, so the step-derived value governs. This is the right
 * call for every revert, reset, step-navigation and form-close path: the protocol is taking
 * ownership of the layout, so a stale manual override must not outrank it.
 */
export function applyProtocolLayouts(
  set: CallbackInterface['set'],
  { caseId, layouts }: CaseScopedArgs
): void {
  publishProtocolLayouts(set, layouts);
  clearUserLayoutOverride(set, caseId);
}

/**
 * Restores a user override that was captured before an edit, along with the layout to render.
 *
 * Only for undo paths whose snapshot recorded that an override genuinely existed -- a layout the
 * user had set outside step mode. When the snapshot has no override, the layout came from the step
 * and {@link applyProtocolLayouts} is the correct call; writing the step's layout back into the
 * override there is exactly the cross-step leak this module exists to prevent.
 */
export function restoreUserLayoutOverride(
  set: CallbackInterface['set'],
  { caseId, layouts }: CaseScopedArgs
): void {
  publishProtocolLayouts(set, layouts);
  if (caseId == null) return;
  set(layoutsForAllCasesState, (prev) => updateCaseLayouts(prev, caseId, layouts));
}

/**
 * Shows the layout of the step the **HP form** has just navigated to.
 *
 * The form is the one surface where {@link applyProtocolLayouts} is the wrong call, and the reason is
 * that `useLayouts` does not read `hangingProtocolLayoutsState` at all -- it merges the layouts memo
 * that `useHangingProtocol` derives from the *applied* protocol, under the case override. So while
 * the form is open the override is the only layer a step switch can reach, and it is already the
 * layer the form's own layout picker writes (`useLayouts.setLayoutsForCase`, routed there because an
 * open HP sidebar makes `isClinicalStepMode` false). Publishing to the protocol layer and clearing
 * the override therefore wrote somewhere nothing reads and dropped the only value that governed --
 * leaving the grid on whatever the applied protocol wanted.
 *
 * In CREATE that is fatal: the protocol being authored does not exist yet, so the derived memo is
 * pinned to the flat matched HP's grid and can never follow the form's steps. Step 1 set to 1x1 came
 * back as the matched protocol's 2x2 (VX-4810). Editing an existing stepped HP hid it, because there
 * the memo is rebuilt per step and already agreed with the arriving step.
 *
 * This does not reopen the cross-step leak the module header describes. That leak is about *clinical*
 * step mode, where the case-scoped override pins every step -- and `useLayouts` ignores the override
 * outright in that mode. The form's closing paths (`restoreInitialLayouts`, cancel) clear it.
 */
export function applyFormStepLayouts(
  set: CallbackInterface['set'],
  { caseId, layouts }: CaseScopedArgs
): void {
  // The protocol layer is published too, so the atom stays coherent for the readers that do consult
  // it -- `saveCurrentStepFromForm` falls back to it when a case has no override.
  publishProtocolLayouts(set, layouts);
  setUserLayoutsForCase(set, { caseId, layouts });
}

/** Drops the user's override for a case, leaving the protocol-derived layout to govern. */
export function clearUserLayoutOverride(
  set: CallbackInterface['set'],
  caseId: Nullable<string>
): void {
  if (caseId == null) return;
  // Scoped to this case: the atom holds every case's overrides, so clearing the whole map would
  // discard other cases' layouts too.
  set(layoutsForAllCasesState, (prev) => omit([caseId], prev));
}

/**
 * Records a layout the user picked, for every window of a case.
 *
 * This is the one legitimate writer of the override layer. It does not touch the protocol layer:
 * the user is overriding what the protocol asked for, not redefining it.
 */
export function setUserLayoutsForCase(
  set: CallbackInterface['set'],
  { caseId, layouts }: CaseScopedArgs
): void {
  if (caseId == null) return;
  set(layoutsForAllCasesState, (prev) => updateCaseLayouts(prev, caseId, layouts));
}

/** Records a layout the user picked, for a single window, preserving the case's other windows. */
export function setUserLayoutForWindow(
  set: CallbackInterface['set'],
  {
    caseId,
    windowId,
    layout,
  }: { caseId: Nullable<string>; windowId: Nullable<string>; layout: Layout }
): void {
  if (caseId == null || windowId == null) return;
  set(layoutsForAllCasesState, (prev) =>
    updateCaseLayouts(prev, caseId, { ...prev[caseId], [windowId]: layout })
  );
}

/**
 * Component-level access to the writers above, with the current case already bound.
 *
 * Replaces `useSetViewerLayouts`, whose contract was "write both atoms" -- it pinned the case
 * override to whatever layout it was given, which is the defect described at the top of this file.
 * `applyProtocolLayouts` is the drop-in replacement and clears the override instead.
 */
export const useViewerLayoutWriters = (): {
  applyProtocolLayouts: (layouts: Layouts) => void;
  applyFormStepLayouts: (layouts: Layouts) => void;
  clearUserLayoutOverride: () => void;
} => {
  const { currentCaseId } = useCurrentCaseContext();

  const apply = useRecoilCallback(
    ({ set }) =>
      (layouts: Layouts) => {
        applyProtocolLayouts(set, { caseId: currentCaseId, layouts });
      },
    [currentCaseId]
  );

  const applyForm = useRecoilCallback(
    ({ set }) =>
      (layouts: Layouts) => {
        applyFormStepLayouts(set, { caseId: currentCaseId, layouts });
      },
    [currentCaseId]
  );

  const clear = useRecoilCallback(
    ({ set }) =>
      () => {
        clearUserLayoutOverride(set, currentCaseId);
      },
    [currentCaseId]
  );

  return {
    applyProtocolLayouts: apply,
    applyFormStepLayouts: applyForm,
    clearUserLayoutOverride: clear,
  };
};

/**
 * Publishing needs no case, so this deliberately does not depend on CurrentCaseProvider.
 *
 * Kept separate from {@link useViewerLayoutWriters} for that reason: useHangingProtocol reaches the
 * case through the non-throwing `useCurrentCaseId()`, and it is consumed by `useLayouts`, which
 * renders in contexts that have no CurrentCaseProvider at all. Folding this into the case-bound
 * hook made `useCurrentCaseContext` throw across a third of the viewer suite.
 */
export const usePublishProtocolLayouts = (): ((layouts: Nullable<Layouts>) => void) =>
  useRecoilCallback(
    ({ set }) =>
      (layouts: Nullable<Layouts>) => {
        publishProtocolLayouts(set, layouts);
      },
    []
  );
